import { languageCodes } from "@services/file-translations/file-translation-validation/language-detection/languages.constant";
import { patterns } from "@services/file-translations/file-translation-validation/language-detection/regex-patterns.constants";

/**
 * Enhanced result interface for language detection
 */
export interface LanguageResult {
  language: string;     // Base language (e.g., "english")
  languageCode: string; // Base language code (e.g., "en")
  confidence: number;   // Confidence percentage
}

/**
 * Result interface including all language matches
 */
export interface LanguageDetectionResult {
  primaryLanguage: LanguageResult;  // The language with highest confidence
  allMatches: LanguageResult[];     // All languages that had matches, sorted by confidence
}

/**
 * Detects the language of text content
 * @param text The text content to analyze
 * @returns The detected language information
 */
export function detectLanguageByRegex(text: string): LanguageDetectionResult {
  const scores: Record<string, number> = {};
  const totalChars = text.length;

  // Calculate scores for each language
  for (const [language, languagePatterns] of Object.entries(patterns)) {
    let score = 0;

    // @ts-ignore
    for (const pattern of languagePatterns) {
      const matches = text.match(pattern);
      if (matches) {
        // For character sets (like Asian languages), calculate percentage of matched characters
        if (pattern.toString().includes('\\u') || pattern.toString().includes('[а-яА-Я]')) {
          const matchLength = matches.join('').length;
          score += (matchLength / totalChars) * 100;
        } else {
          // For word patterns, count occurrences
          score += matches.length;
        }
      }
    }

    scores[language] = score;
  }

  // Calculate total score for confidence percentages
  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);

  // Create array of language results for all matches
  const allLanguageMatches: LanguageResult[] = [];

  // Process each language that had a match
  for (const [language, score] of Object.entries(scores)) {
    // Only include languages with a positive score
    if (score > 0) {
      // Calculate confidence for this language
      const confidence = Math.min(Math.round((score / totalScore) * 100), 100);

      const languageCode = languageCodes[language] || 'unknown';

      allLanguageMatches.push({
        language,
        languageCode,
        confidence
      });
    }
  }

  // Sort matches by confidence (descending)
  allLanguageMatches.sort((a, b) => b.confidence - a.confidence);

  // If we have no matches, return unknown
  if (allLanguageMatches.length === 0) {
    return {
      primaryLanguage: {
        language: 'unknown',
        languageCode: 'unknown',
        confidence: 0
      },
      allMatches: []
    };
  }

  // Return the primary language (highest confidence) and all matches
  return {
    primaryLanguage: allLanguageMatches[0],
    allMatches: allLanguageMatches
  };
}
